import { z } from 'zod';

// ---- Aulas (members area) -----------------------------------------------------------------------

export type VideoSource =
  | { kind: 'youtube'; id: string; embedUrl: string; thumbnailUrl: string; provider: 'YouTube' }
  | { kind: 'vimeo'; id: string; embedUrl: string; provider: 'Vimeo' }
  | { kind: 'file'; src: string; provider: 'Arquivo de vídeo' }
  | { kind: 'embed'; embedUrl: string; provider: string };

const YT_ID = /^[A-Za-z0-9_-]{11}$/;

function youtubeId(u: URL): string | null {
  const host = u.hostname.replace(/^(www|m|music)\./, '');
  if (host === 'youtu.be') return u.pathname.split('/')[1] ?? null;
  if (host !== 'youtube.com' && host !== 'youtube-nocookie.com') return null;
  if (u.pathname === '/watch') return u.searchParams.get('v');
  const [, kind, id] = u.pathname.split('/');
  return kind && ['embed', 'shorts', 'live', 'v'].includes(kind) ? (id ?? null) : null;
}

/** What a lesson's video link points to, and how to play it. Null when it is not an https link. */
export function parseVideoUrl(raw: string | null | undefined): VideoSource | null {
  if (!raw) return null;
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  if (u.protocol !== 'https:') return null;
  const host = u.hostname.replace(/^www\./, '');

  const yt = youtubeId(u);
  if (yt && YT_ID.test(yt)) {
    return {
      kind: 'youtube',
      id: yt,
      embedUrl: `https://www.youtube-nocookie.com/embed/${yt}?rel=0&modestbranding=1&playsinline=1&enablejsapi=1`,
      thumbnailUrl: `https://i.ytimg.com/vi/${yt}/hqdefault.jpg`,
      provider: 'YouTube',
    };
  }

  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const parts = u.pathname.split('/').filter(Boolean);
    const i = parts.findIndex((p) => /^\d+$/.test(p));
    if (i >= 0) {
      const id = parts[i]!;
      // Unlisted videos carry a hash: vimeo.com/123/abc or player.vimeo.com/video/123?h=abc.
      const hash = u.searchParams.get('h') ?? (host === 'vimeo.com' && parts[i + 1] && /^[a-f0-9]+$/i.test(parts[i + 1]!) ? parts[i + 1] : null);
      return { kind: 'vimeo', id, embedUrl: `https://player.vimeo.com/video/${id}?dnt=1&title=0&byline=0&portrait=0${hash ? `&h=${hash}` : ''}`, provider: 'Vimeo' };
    }
  }

  if (/\.(mp4|webm|m4v|mov|ogv)$/i.test(u.pathname)) return { kind: 'file', src: u.toString(), provider: 'Arquivo de vídeo' };

  if (host === 'loom.com') {
    const id = /^\/(share|embed)\/([a-z0-9]+)/i.exec(u.pathname)?.[2];
    if (id) return { kind: 'embed', embedUrl: `https://www.loom.com/embed/${id}`, provider: 'Loom' };
  }
  if (host === 'drive.google.com') {
    const id = /\/file\/d\/([^/]+)/.exec(u.pathname)?.[1];
    if (id) return { kind: 'embed', embedUrl: `https://drive.google.com/file/d/${id}/preview`, provider: 'Google Drive' };
  }
  if (host.endsWith('pandavideo.com.br') || host.endsWith('pandavideo.com')) return { kind: 'embed', embedUrl: u.toString(), provider: 'Panda Video' };
  if (host === 'iframe.mediadelivery.net') return { kind: 'embed', embedUrl: u.toString().replace('/play/', '/embed/'), provider: 'Bunny Stream' };
  return { kind: 'embed', embedUrl: u.toString(), provider: 'Player externo' };
}

/** The lesson image: the uploaded one, else the video's own (YouTube). */
export function lessonThumbnail(thumbnailUrl: string | null | undefined, videoUrl: string | null | undefined): string | null {
  if (thumbnailUrl) return thumbnailUrl;
  const v = parseVideoUrl(videoUrl);
  return v?.kind === 'youtube' ? v.thumbnailUrl : null;
}

/** "12 min", "1 h 05 min", "45 s". */
export function formatLessonDuration(sec: number | null | undefined): string | null {
  if (!sec || sec <= 0) return null;
  if (sec < 60) return `${sec} s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} min`;
}

export interface LessonProgressDTO {
  positionSec: number;
  durationSec: number | null;
  completed: boolean;
  lastViewedAt: string;
}

/** 0–100: finished lessons are 100, started ones by resume point over length. */
export function lessonPercent(progress: LessonProgressDTO | null | undefined, durationSec: number | null | undefined): number {
  if (!progress) return 0;
  if (progress.completed) return 100;
  const len = durationSec || progress.durationSec;
  if (!len) return progress.positionSec > 0 ? 5 : 0;
  return Math.max(0, Math.min(99, Math.round((progress.positionSec / len) * 100)));
}

export interface CourseMaterial {
  title: string;
  url: string;
}

export interface CourseLessonCardDTO {
  id: string;
  moduleId: string;
  title: string;
  description: string | null;
  thumbnailUrl: string | null;
  durationSec: number | null;
  /** Not published yet: only admins see it (preview). */
  draft: boolean;
  progress: LessonProgressDTO | null;
}

export interface CourseModuleDTO {
  id: string;
  title: string;
  description: string | null;
  badge: string | null;
  coverUrl: string | null;
  bannerUrl: string | null;
  draft: boolean;
  lessons: CourseLessonCardDTO[];
}

export interface CoursesCatalogDTO {
  modules: CourseModuleDTO[];
  /** Started, unfinished lessons, most recent first ("Continuar assistindo"). */
  resume: string[];
}

export interface CourseLessonDTO extends CourseLessonCardDTO {
  videoUrl: string | null;
  materials: CourseMaterial[];
  module: CourseModuleDTO;
  prevId: string | null;
  nextId: string | null;
}

export interface AdminCourseLessonDTO {
  id: string;
  moduleId: string;
  title: string;
  description: string | null;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  durationSec: number | null;
  materials: CourseMaterial[];
  published: boolean;
  views: number;
  completions: number;
}

export interface AdminCourseModuleDTO {
  id: string;
  title: string;
  description: string | null;
  badge: string | null;
  coverUrl: string | null;
  bannerUrl: string | null;
  published: boolean;
  lessons: AdminCourseLessonDTO[];
}

export interface AdminCoursesDTO {
  modules: AdminCourseModuleDTO[];
  students: number;
  completions: number;
}

// ---- Admin input --------------------------------------------------------------------------------

const blankToNull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v);

/** An uploaded image (/api/courses/images/…) or an https link. */
export const courseImageUrlSchema = z.preprocess(
  blankToNull,
  z
    .string()
    .trim()
    .max(1000)
    .refine((v) => /^\/api\/courses\/images\/[a-z0-9]+$/.test(v) || /^https:\/\/\S+$/.test(v), 'Use uma imagem enviada ou um link https.')
    .nullable(),
);

const optionalText = (max: number) => z.preprocess(blankToNull, z.string().trim().max(max).nullable());

export const courseModuleInputSchema = z.object({
  title: z.string().trim().min(1, 'Dê um nome ao módulo.').max(120),
  description: optionalText(2000).optional(),
  badge: optionalText(24).optional(),
  coverUrl: courseImageUrlSchema.optional(),
  bannerUrl: courseImageUrlSchema.optional(),
  published: z.boolean().optional(),
});

export const courseLessonInputSchema = z.object({
  moduleId: z.string().min(1).max(40),
  title: z.string().trim().min(1, 'Dê um título à aula.').max(160),
  description: optionalText(10_000).optional(),
  videoUrl: z
    .preprocess(blankToNull, z.string().trim().max(1000).refine((v) => parseVideoUrl(v) !== null, 'Cole o link do vídeo (https).').nullable())
    .optional(),
  thumbnailUrl: courseImageUrlSchema.optional(),
  durationSec: z.preprocess((v) => (v === '' || v === 0 ? null : v), z.number().int().min(1).max(24 * 3600).nullable()).optional(),
  materials: z
    .array(z.object({ title: z.string().trim().min(1, 'Dê um nome ao material.').max(120), url: z.string().trim().url('Link do material inválido.').max(1000).refine((v) => /^https?:\/\//.test(v), 'Link do material inválido.') }))
    .max(20)
    .optional(),
  published: z.boolean().optional(),
});

export const courseOrderSchema = z.object({ ids: z.array(z.string().min(1).max(40)).min(1).max(500) });

export const lessonProgressInputSchema = z.object({
  positionSec: z.number().int().min(0).max(24 * 3600).optional(),
  durationSec: z.number().int().min(1).max(24 * 3600).optional(),
  completed: z.boolean().optional(),
});
